import { motion, useInView, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ArrowDown, Globe, Bot, Cpu, Printer, Cloud, Layers } from "lucide-react";
import { useRef, useEffect } from "react";
import MagneticButton from "./MagneticButton";

function AnimatedNumber({ value }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  const numMatch = value.match(/^(\d+)(.*)$/);
  
  const spring = useSpring(0, { bounce: 0, duration: 2000 });
  const display = useTransform(spring, (current) => Math.floor(current));

  useEffect(() => {
    if (isInView && numMatch) {
      spring.set(parseInt(numMatch[1], 10));
    }
  }, [isInView, numMatch, spring]);

  if (!numMatch) return <>{value}</>;

  return (
    <span ref={ref}>
      <motion.span>{display}</motion.span>
      {numMatch[2]}
    </span>
  );
}

const DISCIPLINES = [
  { icon: Globe, label: "Web & Apps", color: "cyan" },
  { icon: Bot, label: "AI Products", color: "purple" },
  { icon: Cloud, label: "SaaS Platforms", color: "cyan" },
  { icon: Cpu, label: "Hardware & IoT", color: "purple" },
  { icon: Printer, label: "3D Printing", color: "cyan" },
  { icon: Layers, label: "Full Product", color: "purple" },
];

export default function Hero() {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 150]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -100]);
  const y3 = useTransform(scrollY, [0, 1000], [0, 200]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 pb-20"
    >
      {/* Animated background */}
      <div className="absolute inset-0 tech-grid opacity-60" />
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink-0 pointer-events-none" />
      <div className="noise" />

      {/* Floating accent blobs with parallax */}
      <motion.div style={{ y: y1 }} className="absolute inset-0 pointer-events-none">
        <motion.div
          aria-hidden
          className="absolute -top-20 -right-32 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.07) 0%, transparent 60%)" }}
          animate={{ y: [0, 24, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      
      <motion.div style={{ y: y2 }} className="absolute inset-0 pointer-events-none">
        <motion.div
          aria-hidden
          className="absolute top-1/2 -left-48 w-[500px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(108,99,255,0.09) 0%, transparent 60%)" }}
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <motion.div style={{ y: y3 }} className="absolute inset-0 pointer-events-none">
        <motion.div
          aria-hidden
          className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.05) 0%, transparent 60%)" }}
          animate={{ y: [0, 18, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full">

        {/* Top badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8 shadow-2xl shadow-accent-cyan/10"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-cyan opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-cyan" />
          </span>
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-zinc-300">
            Full-Product Studio · Q2–Q3 2026
          </span>
        </motion.div>

        {/* Discipline pills */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex flex-wrap gap-2 mb-10"
        >
          {DISCIPLINES.map(({ icon: Icon, label, color }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.06 }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-mono uppercase tracking-[0.15em] ${
                color === "cyan"
                  ? "border-accent-cyan/20 bg-accent-cyan/5 text-accent-cyan/80"
                  : "border-accent-purple/20 bg-accent-purple/5 text-accent-purple/80"
              }`}
            >
              <Icon size={10} strokeWidth={2} />
              {label}
            </motion.div>
          ))}
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12 } },
          }}
          className="font-display text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.92] tracking-tight max-w-5xl text-white"
          data-testid="hero-heading"
        >
          {[
            "One Studio.",
            <span key="r2" className="text-zinc-500">Every Discipline.</span>,
          ].map((row, i) => (
            <motion.span
              key={i}
              variants={{
                hidden: { opacity: 0, y: 32 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                },
              }}
              className="block"
            >
              {row}
            </motion.span>
          ))}
        </motion.h1>

        {/* Sub headline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-8 max-w-2xl text-base md:text-xl text-zinc-400 leading-relaxed font-light"
          data-testid="hero-subheading"
        >
          We build complete products — web apps, AI-native systems, SaaS platforms,
          embedded hardware, and 3D-printed enclosures. Discovery to deployment.
          No handoffs. No guesswork.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <MagneticButton>
            <a
              href="#services"
              data-testid="hero-cta-explore"
              className="group relative inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-black text-sm font-semibold overflow-hidden transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_-10px_rgba(255,255,255,0.5)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                Explore all capabilities
                <ArrowRight size={16} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
          </MagneticButton>
          <MagneticButton>
            <a
              href="#cta"
              data-testid="hero-cta-start"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white text-sm font-medium hover:bg-white/10 transition-all hover:border-white/20"
            >
              Start a project
            </a>
          </MagneticButton>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-4xl"
          data-testid="hero-stats"
        >
          {[
            ["40+", "Products Shipped"],
            ["6 Wks", "Avg. Launch Time"],
            ["100%", "Integrated Team Delivery"],
            ["End-to-End", "Delivery Model"],
          ].map(([n, l]) => (
            <div key={l} className="border-t border-white/10 pt-4">
              <div className="font-display text-2xl md:text-3xl text-white">
                <AnimatedNumber value={n} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#services"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 hover:text-white transition-colors"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce-arrow" strokeWidth={2} />
      </a>
    </section>
  );
}
